import { Fragment, ReactElement } from 'react';
import { Container, Checkbox, Button, styled } from '@mui/material';
import {
  formatDate,
  FocusLabelButton,
  parseDate,
  ValueLabel,
  isRequestNotCancellable,
  variables,
  mapRole,
} from '../common';
import { IRequestStatus, propertyMap } from '.';
import { pathOr } from 'rambda';
import {
  map as lmap,
  compose as lcompose,
  compact as lcompact,
  replace as lreplace,
} from 'lodash/fp';
import { left, right, fold } from 'fp-ts/lib/Either';
import { flow } from 'fp-ts/function';

const sortable =
  (
    propertyMap: Record<
      'requestId' | 'reqType' | 'reqRole' | 'status' | 'createdAt',
      string
    >
  ) =>
  (name: string) =>
    pathOr('', name)(propertyMap) !== '';

export const render =
  (s: any) =>
  (properties: ValueLabel<keyof IRequestStatus>[]) =>
  ({
    sortBy,
    sortDirection,
    toggleSort,
    disableOperationsHelpDsk,
    selectedRequestIds,
    toggleRequestSelection,
    confirmCancelRequests,
  }: {
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    toggleSort: (name: string) => void;
    disableOperationsHelpDsk: boolean;
    selectedRequestIds?: number[];
    toggleRequestSelection?: (payload: IRequestStatus) => void;
    confirmCancelRequests?: () => void;
  }) =>
    lcompose(
      fold(
        () => <Container maxWidth="lg">Empty</Container>,

        deaRegistrations => (
          <Article>
            {lmap<ValueLabel<string>, ReactElement>(({ value, label }) => (
              <h4 key={label}>
                {value === 'action' ? (
                  selectedRequestIds?.length === 0 ? (
                    `Action`
                  ) : (
                    <Button
                      onClick={confirmCancelRequests}
                      disabled={
                        selectedRequestIds?.length === 0 ||
                        disableOperationsHelpDsk
                      }
                      variant="contained">
                      Cancel Request
                    </Button>
                  )
                ) : sortable(propertyMap)(value) ? (
                  <FocusLabelButton
                    name={pathOr('', value)(propertyMap)}
                    label={label}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onClick={() => toggleSort(pathOr('', value)(propertyMap))}
                  />
                ) : (
                  <span>{label}</span>
                )}
              </h4>
            ))(properties)}

            {lmap<IRequestStatus, ReactElement>(
              lcompose(
                ([a, b]) => <Fragment key={a.requestId}>{b}</Fragment>,
                ([a, b]) => [a, lcompact(b)],
                a => [
                  a,
                  [
                    ...lmap<
                      ValueLabel<keyof IRequestStatus>,
                      ReactElement | undefined
                    >(({ value }) => (
                      <SpanPV key={`${a.requestId}-${value}`}>
                        {show(
                          a,
                          toggleRequestSelection,
                          selectedRequestIds
                        )(value)}
                      </SpanPV>
                    ))(properties),
                  ],
                ]
              )
            )(deaRegistrations)}
          </Article>
        )
      ),

      (requests: IRequestStatus[]) =>
        requests.length === 0 ? left(requests) : right(requests)
    );

export const show =
  (
    a: IRequestStatus,
    toggleRequestSelection?: (payload: IRequestStatus) => void,
    selectedRequestIds?: any[]
  ) =>
  (value: keyof IRequestStatus): string | ReactElement | undefined => {
    if (value === 'status') {
      return lreplace(/_/g, ' ')(a[value]);
    }

    if (value === 'deaNumbers') {
      return (
        <>
          {lmap<string, ReactElement>(a => <span key={a}>{a} </span>)(a[value])}
        </>
      );
    }

    if (value === 'createdAt') {
      return flow(parseDate, formatDate)(a[value]);
    }

    if (value === 'action') {
      return (
        <span key="expand">
          <Checkbox
            color="primary"
            disabled={isRequestNotCancellable(a)}
            onClick={toggleRequestSelection?.bind(null, a)}
            checked={selectedRequestIds?.includes(a.requestId)}
          />
        </span>
      );
    }

    // if (value === 'beneficiaryEmail') {
    //   return a.beneficiaryEmail ? (
    //     <span>{a.beneficiaryEmail}</span>
    //   ) : (
    //     <span>
    //       {a.beneficiaryFirstName} {a.beneficiaryLastName}
    //     </span>
    //   );
    // }

    if (value === 'reqRole') {
      return mapRole(a.reqRole);
    }

    if (value === 'beneficiaryEmail') {
      return (
        <>
          <span>
            {a.beneficiaryFirstName} {a.beneficiaryLastName} <br></br>
            {a.beneficiaryEmail}
          </span>
        </>
      );
    }
    if (value === 'activeApproverEmail') {
      return (
        <>
          <span>
            {a.activeFirstName} {a.activeLastName} <br></br>
            {a.activeApproverEmail}
          </span>
        </>
      );
    }
    if (value === 'requestorEmail') {
      if (a.requestorEmail === 'RA Group') {
        return `Registration Authority`;
      }
      if (a.requestorEmail === 'DI Group') {
        return `Diversion Investigators`;
      }
    }

    return a[value] as string;
  };

const Article = styled('article')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  '&>*+*': {
    marginTop: '1rem',
  },

  '@media (min-width: 60em)': {
    display: 'grid',
    gridTemplateColumns: 'repeat(11, auto) ',
    gridAutoRows: 'minmax(auto, 5em)',

    gap: '1px',

    backgroundColor: variables.grayLighter,
    border: variables.cardBorder,
    borderRadius: variables.cardBorderRadius,

    '&>*': {
      backgroundColor: '#fff',
      padding: '1rem',
    },

    '&>*+*': {
      marginTop: '0rem',
    },
  },
}));

const SpanPV = styled('span')(({ theme }) => ({
  overflow: 'auto',
}));
